import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api, serviceRecordsApi, clientsApi, staffApi } from '@/services/api';
import { downloadCSV } from '@/utils/csv';
import { HiOutlineCalendar, HiOutlinePlus, HiOutlineDownload, HiX } from 'react-icons/hi';

interface ServiceRecord {
  record: {
    id: string;
    serviceDate: string;
    serviceType: string;
    startTime: string;
    endTime: string;
    activityContent: string;
    supportDetails: string;
    clientCondition: string;
    notes: string;
  };
  client: {
    id: string;
    clientNumber: string;
    lastName: string;
    firstName: string;
  } | null;
  staff: {
    id: string;
    lastName: string;
    firstName: string;
  } | null;
}

interface Client {
  id: string;
  lastName: string;
  firstName: string;
  clientNumber: string;
}

interface Staff {
  id: string;
  lastName: string;
  firstName: string;
}

const serviceTypeLabels: Record<string, string> = {
  training: '訓練',
  work: '作業',
  counseling: '相談',
  outing: '外出',
  other: 'その他',
};

export default function ServiceRecordsPage() {
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [isExporting, setIsExporting] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    clientId: '',
    staffId: '',
    serviceDate: new Date().toISOString().split('T')[0],
    serviceType: 'training',
    startTime: '09:00',
    endTime: '17:00',
    activityContent: '',
    supportDetails: '',
    clientCondition: '',
    notes: '',
  });
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<{ data: ServiceRecord[] }>({
    queryKey: ['service-records', { startDate, endDate }],
    queryFn: () => api.get(`/api/v1/service-records?startDate=${startDate}&endDate=${endDate}`) as Promise<{ data: ServiceRecord[] }>,
  });

  const { data: clientsData } = useQuery<{ data: Client[] }>({
    queryKey: ['clients-list'],
    queryFn: () => clientsApi.list({ limit: 100 }) as Promise<{ data: Client[] }>,
  });

  const { data: staffData } = useQuery<{ data: Staff[] }>({
    queryKey: ['staff-list'],
    queryFn: () => staffApi.list({ limit: 100 }) as Promise<{ data: Staff[] }>,
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => serviceRecordsApi.create(data),
    onSuccess: () => {
      toast.success('サービス提供記録を作成しました');
      queryClient.invalidateQueries({ queryKey: ['service-records'] });
      setShowCreateModal(false);
      setFormData({
        clientId: '',
        staffId: '',
        serviceDate: new Date().toISOString().split('T')[0],
        serviceType: 'training',
        startTime: '09:00',
        endTime: '17:00',
        activityContent: '',
        supportDetails: '',
        clientCondition: '',
        notes: '',
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || '作成に失敗しました');
    },
  });

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.clientId) {
      toast.error('利用者を選択してください');
      return;
    }
    if (!formData.serviceDate) {
      toast.error('日付を入力してください');
      return;
    }
    createMutation.mutate(formData);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await downloadCSV(`/api/v1/export/service-records?startDate=${startDate}&endDate=${endDate}`);
      toast.success('CSVをダウンロードしました');
    } catch (error) {
      toast.error('ダウンロードに失敗しました');
    } finally {
      setIsExporting(false);
    }
  };

  const records = data?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">サービス提供記録</h1>
          <p className="text-gray-600 mt-1">日々の支援内容を記録します</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleExport}
            disabled={isExporting}
            className="btn-secondary"
          >
            <HiOutlineDownload className="w-5 h-5 mr-2" />
            {isExporting ? 'エクスポート中...' : 'CSVエクスポート'}
          </button>
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            <HiOutlinePlus className="w-5 h-5 mr-2" />
            新規記録
          </button>
        </div>
      </div>

      {/* 作成モーダル */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">サービス提供記録 新規作成</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <HiX className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateSubmit} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">利用者 *</label>
                  <select
                    value={formData.clientId}
                    onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                    className="input w-full"
                    required
                  >
                    <option value="">選択してください</option>
                    {clientsData?.data?.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.lastName} {client.firstName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">担当職員</label>
                  <select
                    value={formData.staffId}
                    onChange={(e) => setFormData({ ...formData, staffId: e.target.value })}
                    className="input w-full"
                  >
                    <option value="">選択してください</option>
                    {staffData?.data?.map((staff) => (
                      <option key={staff.id} value={staff.id}>
                        {staff.lastName} {staff.firstName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="label">日付 *</label>
                  <input
                    type="date"
                    value={formData.serviceDate}
                    onChange={(e) => setFormData({ ...formData, serviceDate: e.target.value })}
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="label">開始時刻</label>
                  <input
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="label">終了時刻</label>
                  <input
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    className="input w-full"
                  />
                </div>
              </div>
              <div>
                <label className="label">種別</label>
                <select
                  value={formData.serviceType}
                  onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
                  className="input w-full"
                >
                  <option value="training">訓練</option>
                  <option value="work">作業</option>
                  <option value="counseling">相談</option>
                  <option value="outing">外出</option>
                  <option value="other">その他</option>
                </select>
              </div>
              <div>
                <label className="label">活動内容</label>
                <textarea
                  value={formData.activityContent}
                  onChange={(e) => setFormData({ ...formData, activityContent: e.target.value })}
                  className="input w-full"
                  rows={2}
                  placeholder="本日の活動内容を記入してください"
                />
              </div>
              <div>
                <label className="label">支援詳細</label>
                <textarea
                  value={formData.supportDetails}
                  onChange={(e) => setFormData({ ...formData, supportDetails: e.target.value })}
                  className="input w-full"
                  rows={2}
                  placeholder="提供した支援の詳細を記入してください"
                />
              </div>
              <div>
                <label className="label">利用者の様子</label>
                <textarea
                  value={formData.clientCondition}
                  onChange={(e) => setFormData({ ...formData, clientCondition: e.target.value })}
                  className="input w-full"
                  rows={2}
                  placeholder="利用者の様子や状態を記入してください"
                />
              </div>
              <div>
                <label className="label">備考</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input w-full"
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary">
                  キャンセル
                </button>
                <button type="submit" disabled={createMutation.isPending} className="btn-primary">
                  {createMutation.isPending ? '作成中...' : '作成'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <HiOutlineCalendar className="w-5 h-5 text-gray-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input"
              />
              <span className="text-gray-500">〜</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input"
              />
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>日付</th>
                <th>利用者</th>
                <th>担当職員</th>
                <th>種別</th>
                <th>時間</th>
                <th>活動内容</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-gray-500">
                    読み込み中...
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-gray-500">
                    サービス提供記録がありません
                  </td>
                </tr>
              ) : (
                records.map(({ record, client, staff }) => (
                  <tr key={record.id}>
                    <td>{record.serviceDate}</td>
                    <td>
                      {client ? `${client.lastName} ${client.firstName}` : '-'}
                    </td>
                    <td>
                      {staff ? `${staff.lastName} ${staff.firstName}` : '-'}
                    </td>
                    <td>{serviceTypeLabels[record.serviceType] || record.serviceType}</td>
                    <td>
                      {record.startTime && record.endTime 
                        ? `${record.startTime} - ${record.endTime}` 
                        : '-'}
                    </td>
                    <td className="max-w-xs truncate">{record.activityContent || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
